// Global Instructions Rule Applied!
//
// Consolidated funds (fund groups) HTTP layer. URLs come exclusively from
// core/utils/endpoints.ts; every response is Zod-validated before it reaches
// callers. The subscription save surfaces the backend's 409
// below_called_warning envelope via `belowCalledWarning` so the UI can
// confirm-through with force=true.

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  FundGroupCallDetail,
  FundGroupCallRow,
  FundGroupTransferDefaults,
  FundGroupTransferDetail,
  FundGroupTransferDraftResult,
  FundGroupTransferPool,
  FundGroupTransferRow,
  FundGroupDashboard,
  FundGroupDetail,
  FundGroupEqualizationPreview,
  FundGroupFeeReallocation,
  FundGroupFeeReallocationAdvanceResult,
  FundGroupGridRow,
  FundGroupPosition,
  FundGroupSubscriptionRow,
  validateFundGroupCallDetail,
  validateFundGroupCallRows,
  validateFundGroupTransferDefaults,
  validateFundGroupTransferDetail,
  validateFundGroupTransferDraftResult,
  validateFundGroupTransferPool,
  validateFundGroupTransferRows,
  validateFundGroupDashboard,
  validateFundGroupDetail,
  validateFundGroupEqualizationPreview,
  validateFundGroupFeeReallocation,
  validateFundGroupFeeReallocationAdvanceResult,
  validateFundGroupGridRows,
  FundGroupDistributionRow,
  validateFundGroupDistributionRows,
  validateFundGroupPosition,
  validateFundGroupSubscriptionRows
} from '../../../state_manager/data_models/fund_group.ts'
import {
  FundGroupGlDetailRow,
  FundGroupLedgerMember,
  FundGroupMemberFinYear,
  FundGroupTrialBalanceRow,
  validateFundGroupGlDetailRows,
  validateFundGroupLedgerMembers,
  validateFundGroupTrialBalanceRows
} from '../../../state_manager/data_models/fund_group_ledger.ts'
import { FundLedgerFinYear, FundLedgerTotals } from '../../../state_manager/data_models/fund_ledger.ts'
import {
  FundLpCapitalAccountHeader,
  FundPartnersCapitalTotal,
  FundStatementLayout,
  FundStatementMeta,
  validateFundPartnersCapitalRows,
  validateFundStatementColumns,
  validateFundStatementLpOptions,
  validateFundStatementRows,
  validateFundStatementTypeOptions
} from '../../../state_manager/data_models/fund_statements.ts'
import { validateInvestorStatementChecks } from '../../../state_manager/data_models/investor_statement_checks.ts'
import { FundStatementSettings, validateFundStatementSettings } from '../../../state_manager/data_models/fund_statement_settings.ts'
import {
  DistributionDetail,
  DistributionSourceOption,
  FundGroupDistributionSavePayload,
  validateDistributionDetail,
  validateDistributionSourceOptions
} from '../../../state_manager/data_models/distribution_detail.ts'
import { FundWaterfallPosition, validateFundWaterfallPosition } from '../../../state_manager/data_models/fund_waterfall_position_record.ts'
// response contracts shared with the per-fund fetchers so the Ledger /
// Statements tabs swap services 1:1 in group mode (types only)
import type {
  FundLpCapitalAccountResponse,
  FundPartnersCapitalResponse,
  FundStatementOptionsResponse,
  FundStatementResponse,
  InvestorStatementChecksResponse
} from '../../funds/services/fundsService.ts'
import { FilterValue, SortConfig } from '../../global_configs/tableViewPreferences.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

export interface FundGroupSubscriptionsPaginatedResponse {
  data: FundGroupSubscriptionRow[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface SubscriptionAllocationInput {
  fund_group_member_id: string
  amount: number
  /** Satellite only: the allocation's own status (null = inherit the subscription's). */
  status_id?: number | null
  /** Satellite only: the allocation's own close date, 'YYYY-MM-DD' (null = inherit). */
  date_closed?: string | null
}

/** PARTIAL by design: fg__save_subscription leaves an absent key alone. Create
 *  sends fund_group_id + investor_id OR new_investor. */
export interface SubscriptionSavePayload {
  id?: string
  fund_group_id?: string
  investor_id?: string
  /** create only — the investor entity is created under its account first */
  new_investor?: {
    account_id: string
    name: string
    legal_name?: string | null
    investor_classification_id?: number | null
    sbic_investor_type_id?: number | null
  }
  amount?: number
  currency_id?: string
  status_id?: number | null
  date_committed?: string | null
  date_closed?: string | null
  /** The general partner's own commitment. Present = set on the consolidated
   *  commitment and on its commitments in the member funds; with `id` alone
   *  it is the only thing the save changes. */
  is_gp_commitment?: boolean
  /** The commitment every member fund's carried interest goes to. Present =
   *  set on the consolidated commitment and on its commitments in the member
   *  funds; the one case where `amount` may be 0. */
  is_carry_recipient?: boolean
  /** The additional notice documents, written to the commitment in every
   *  member fund: false = the investor's, true = the set below. */
  notice_supplements_overridden?: boolean
  notice_supplement_template_ids?: number[]
  /** The full allocation set; a member fund left out loses its allocation. */
  allocations?: SubscriptionAllocationInput[]
}

export const fetchFundGroups = async (
  accessToken: string | null = null
): Promise<{ data: FundGroupGridRow[]; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.FILTER), {
      method: API_ROUTES.FUND_GROUPS.FILTER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: {} })
    })
    if (!response.ok) throw new Error(`Failed to fetch fund groups: ${response.status}`)
    const json = (await response.json()) as { data?: unknown[] }
    return { data: validateFundGroupGridRows(json.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund groups:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupDashboard = async (
  fundGroupId: string,
  accessToken: string | null = null
): Promise<{ data: FundGroupDashboard | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.DASHBOARD, { id: fundGroupId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.DASHBOARD.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch fund group: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundGroupDashboard(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund group dashboard:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupDetail = async (
  fundGroupId: string,
  accessToken: string | null = null
): Promise<{ data: FundGroupDetail | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.DETAIL, { id: fundGroupId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.DETAIL.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch fund group detail: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundGroupDetail(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund group detail:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupSubscriptionsFiltered = async (
  fundGroupId: string,
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null
): Promise<FundGroupSubscriptionsPaginatedResponse> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUND_GROUPS.SUBSCRIPTIONS_FILTER,
      { id: fundGroupId },
      {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.SUBSCRIPTIONS_FILTER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({
        data: {
          page,
          page_size: pageSize ?? 200,
          sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
          filters: filters.map((f) => ({ key: f.key, value: f.value }))
        }
      })
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch consolidated commitments: ${response.status}`)
    }
    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rows = Array.isArray(json.data) ? json.data : []
    return {
      data: validateFundGroupSubscriptionRows(rows),
      hasMore: Boolean(json.has_more),
      totalCount: json.count ?? rows.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching subscriptions:', error)
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchFundGroupSubscriptionsFilterOptions = async (
  fundGroupId: string,
  accessToken: string | null = null
): Promise<{ data: Record<string, Array<{ value: string; label: string }>>; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUND_GROUPS.SUBSCRIPTIONS_FILTER_OPTIONS,
      { id: fundGroupId },
      {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.SUBSCRIPTIONS_FILTER_OPTIONS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch filter options: ${response.status}`)
    const json = (await response.json()) as { data?: Record<string, Array<{ value: string; label: string }>> }
    return { data: json.data ?? {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching filter options:', error)
    return { data: {}, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const saveFundGroupSubscription = async (
  payload: SubscriptionSavePayload,
  force: boolean = false,
  accessToken: string | null = null
): Promise<{
  success: boolean
  /** the saved consolidated commitment (a new form navigates to it) */
  fundGroupCommitmentId?: string
  belowCalledWarning?: { pockets: Array<Record<string, unknown>> }
  error?: string
}> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.SAVE_SUBSCRIPTION), {
      method: API_ROUTES.FUND_GROUPS.SAVE_SUBSCRIPTION.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { ...payload, force } })
    })
    const json = (await response.json().catch(() => ({}))) as {
      data?: { status?: string; pockets?: Array<Record<string, unknown>>; fund_group_commitments__id?: string }
      error?: string
      message?: string
    }
    if (response.ok) {
      const savedId = json.data?.fund_group_commitments__id
      return { success: true, fundGroupCommitmentId: typeof savedId === 'string' ? savedId : undefined }
    }
    if (response.status === 409 && json.data?.status === 'below_called_warning') {
      return { success: false, belowCalledWarning: { pockets: json.data.pockets ?? [] } }
    }
    return { success: false, error: json.error || json.message || `Failed to save consolidated commitment: ${response.status}` }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving subscription:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const deleteFundGroupSubscription = async (
  subscriptionId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUND_GROUPS.DELETE_SUBSCRIPTION,
      { id: subscriptionId },
      {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.DELETE_SUBSCRIPTION.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) {
      const json = (await response.json().catch(() => ({}))) as { error?: string; message?: string }
      return { success: false, error: json.error || json.message || `Failed to delete consolidated commitment: ${response.status}` }
    }
    return { success: true }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error deleting subscription:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupCallPosition = async (
  fundGroupId: string,
  accessToken: string | null = null
): Promise<{ data: FundGroupPosition | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.CALL_POSITION, { id: fundGroupId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.CALL_POSITION.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch call position: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundGroupPosition(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching call position:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// ---------------------------------------------------------------------------
// Consolidated call orchestration (phase 2)
// ---------------------------------------------------------------------------

export interface FundGroupCallSavePayload {
  id?: string
  fund_group_id?: string
  name?: string | null
  call_date?: string | null
  due_date?: string | null
  item_type_id?: string | null
  description?: string | null
  total_amount?: number | null
  total_percentage?: number | null
}

export const fetchFundGroupCalls = async (
  fundGroupId: string,
  accessToken: string | null = null
): Promise<{ data: FundGroupCallRow[]; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.CALLS, { id: fundGroupId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.CALLS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch consolidated calls: ${response.status}`)
    const json = (await response.json()) as { data?: unknown[] }
    return { data: validateFundGroupCallRows(json.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching consolidated calls:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupCallDetail = async (
  fundGroupCallId: string,
  accessToken: string | null = null
): Promise<{ data: FundGroupCallDetail | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.CALL_DETAIL, { id: fundGroupCallId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.CALL_DETAIL.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch consolidated call: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundGroupCallDetail(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching consolidated call detail:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const saveFundGroupCall = async (
  payload: FundGroupCallSavePayload,
  accessToken: string | null = null
): Promise<{ success: boolean; data?: FundGroupCallDetail; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.CALL_SAVE), {
      method: API_ROUTES.FUND_GROUPS.CALL_SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    if (!response.ok) {
      const json = (await response.json().catch(() => ({}))) as { error?: string; message?: string }
      return { success: false, error: json.error || json.message || `Failed to save consolidated call: ${response.status}` }
    }
    // the save returns fgc__call_detail — the call-form flow needs the
    // materialised primary_call id to swap the tab onto the created record
    const json = (await response.json()) as { data?: unknown }
    return { success: true, data: validateFundGroupCallDetail(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving consolidated call:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

const postFundGroupCallAction = async (
  route: { path: string; method: string },
  fundGroupCallId: string,
  accessToken: string | null,
  body?: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      route as (typeof API_ROUTES.FUND_GROUPS)['CALL_ISSUE'],
      { id: fundGroupCallId },
      {}
    )
    const response = await fetch(url, {
      method: route.method,
      headers: authHeaders(accessToken),
      ...(body ? { body: JSON.stringify({ data: body }) } : {})
    })
    if (!response.ok) {
      const json = (await response.json().catch(() => ({}))) as { error?: string; message?: string }
      return { success: false, error: json.error || json.message || `Action failed: ${response.status}` }
    }
    return { success: true }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error on consolidated call action:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const issueFundGroupCall = (fundGroupCallId: string, accessToken: string | null = null) =>
  postFundGroupCallAction(API_ROUTES.FUND_GROUPS.CALL_ISSUE, fundGroupCallId, accessToken)

export const revertFundGroupCall = (fundGroupCallId: string, accessToken: string | null = null) =>
  postFundGroupCallAction(API_ROUTES.FUND_GROUPS.CALL_REVERT, fundGroupCallId, accessToken)

export const refreshFundGroupCallSplit = (fundGroupCallId: string, accessToken: string | null = null) =>
  postFundGroupCallAction(API_ROUTES.FUND_GROUPS.CALL_REFRESH_SPLIT, fundGroupCallId, accessToken)

export const deleteFundGroupCall = (fundGroupCallId: string, accessToken: string | null = null) =>
  postFundGroupCallAction(API_ROUTES.FUND_GROUPS.CALL_DELETE, fundGroupCallId, accessToken)

// ---------------------------------------------------------------------------
// Intercompany transfers (phase 2, v2 model)
// ---------------------------------------------------------------------------

// A transfer is a Draft header + its derived satellite call, edited in the
// standard Capital Call form; the modal only picks the satellite, the date
// and the source (the pool, or an equalization call).
export interface FundGroupTransferDraftPayload {
  fund_group_id: string
  fund_id: string
  transfer_date: string
  /** NULL / omitted = the pool of every issued call; else an equalization call */
  fund_group_call_id?: string | null
  notes?: string | null
}

export const fetchFundGroupTransfers = async (
  fundGroupId: string,
  accessToken: string | null = null
): Promise<{ data: FundGroupTransferRow[]; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.TRANSFERS, { id: fundGroupId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.TRANSFERS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch transfers: ${response.status}`)
    const json = (await response.json()) as { data?: unknown[] }
    return { data: validateFundGroupTransferRows(json.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching transfers:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupTransferDetail = async (
  transferId: string,
  accessToken: string | null = null
): Promise<{ data: FundGroupTransferDetail | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.TRANSFER_DETAIL, { id: transferId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.TRANSFER_DETAIL.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch transfer: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundGroupTransferDetail(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching transfer detail:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupTransferDefaults = async (
  fundGroupId: string,
  fundId: string | null = null,
  fundGroupCallId: string | null = null,
  accessToken: string | null = null
): Promise<{ data: FundGroupTransferDefaults | null; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUND_GROUPS.TRANSFER_DEFAULTS,
      { id: fundGroupId },
      {
        ...(fundId ? { fund_id: fundId } : {}),
        ...(fundGroupCallId ? { fund_group_call_id: fundGroupCallId } : {})
      }
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.TRANSFER_DEFAULTS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch transfer defaults: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundGroupTransferDefaults(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching transfer defaults:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupTransferPool = async (
  fundGroupId: string,
  accessToken: string | null = null
): Promise<{ data: FundGroupTransferPool | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.TRANSFER_POOL, { id: fundGroupId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.TRANSFER_POOL.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch the transfer pool: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundGroupTransferPool(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching transfer pool:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const createFundGroupTransferDraft = async (
  payload: FundGroupTransferDraftPayload,
  accessToken: string | null = null
): Promise<{ success: boolean; data?: FundGroupTransferDraftResult; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.TRANSFER_CREATE), {
      method: API_ROUTES.FUND_GROUPS.TRANSFER_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; error?: string; message?: string }
    if (!response.ok) {
      return { success: false, error: json.error || json.message || `Failed to create the transfer: ${response.status}` }
    }
    return { success: true, data: validateFundGroupTransferDraftResult(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error creating draft transfer:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const bookFundGroupTransfer = async (
  transferId: string,
  force: boolean = false,
  accessToken: string | null = null
): Promise<{
  success: boolean
  overCalledWarning?: { message: string }
  error?: string
}> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.TRANSFER_BOOK, { id: transferId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.TRANSFER_BOOK.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { force } })
    })
    const json = (await response.json().catch(() => ({}))) as {
      data?: { status?: string; status_message?: string }
      error?: string
      message?: string
    }
    if (response.ok) return { success: true }
    if (response.status === 409 && json.data?.status === 'over_called_warning') {
      return {
        success: false,
        overCalledWarning: { message: json.data.status_message ?? 'Transfer exceeds capital called to date' }
      }
    }
    return { success: false, error: json.error || json.message || `Failed to book the transfer: ${response.status}` }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error booking transfer:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const revertFundGroupTransfer = (transferId: string, accessToken: string | null = null) =>
  postFundGroupCallAction(API_ROUTES.FUND_GROUPS.TRANSFER_REVERT, transferId, accessToken)

export const deleteFundGroupTransfer = (transferId: string, accessToken: string | null = null) =>
  postFundGroupCallAction(API_ROUTES.FUND_GROUPS.TRANSFER_DELETE, transferId, accessToken)

// ---------------------------------------------------------------------------
// Late-joiner fee reallocation
// ---------------------------------------------------------------------------

export const fetchFundGroupFeeReallocation = async (
  fundGroupId: string,
  asOf: string | null = null,
  accessToken: string | null = null
): Promise<{ data: FundGroupFeeReallocation | null; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUND_GROUPS.FEE_REALLOCATION,
      { id: fundGroupId },
      asOf ? { as_of: asOf } : {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.FEE_REALLOCATION.method,
      headers: authHeaders(accessToken)
    })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; error?: string; message?: string }
    if (!response.ok) {
      return { data: null, error: json.error || json.message || `Failed to fetch the fee reallocation: ${response.status}` }
    }
    return { data: validateFundGroupFeeReallocation(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fee reallocation:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export interface FundGroupFeeReallocationPayload {
  fund_group_id: string
  fund_id: string
  as_of: string
}

export const advanceFundGroupFeeReallocation = async (
  payload: FundGroupFeeReallocationPayload,
  accessToken: string | null = null
): Promise<{ success: boolean; data?: FundGroupFeeReallocationAdvanceResult; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.FEE_REALLOCATION_ADVANCE), {
      method: API_ROUTES.FUND_GROUPS.FEE_REALLOCATION_ADVANCE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; error?: string; message?: string }
    if (!response.ok) {
      return { success: false, error: json.error || json.message || `Failed to draft the reallocation: ${response.status}` }
    }
    return { success: true, data: validateFundGroupFeeReallocationAdvanceResult(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error drafting fee reallocation:', error)
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const deleteFundGroupFeeReallocation = (eventId: string, accessToken: string | null = null) =>
  postFundGroupCallAction(API_ROUTES.FUND_GROUPS.FEE_REALLOCATION_DELETE, eventId, accessToken)

export const fetchFundGroupEqualizationPreview = async (
  fundGroupId: string,
  accessToken: string | null = null
): Promise<{ data: FundGroupEqualizationPreview | null; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUND_GROUPS.EQUALIZATION_PREVIEW,
      { id: fundGroupId },
      {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.EQUALIZATION_PREVIEW.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch equalization preview: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundGroupEqualizationPreview(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching equalization preview:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// ---------------------------------------------------------------------------
// Consolidated ledger & statements (reads only). Response contracts mirror
// the fundsService twins so the shared Ledger/Statements tabs swap fetchers
// 1:1 in group mode. Statements rows reuse the fund_statements.ts
// validators — the group RPCs emit byte-identical shapes.
// ---------------------------------------------------------------------------

export interface FundGroupTrialBalanceResponse {
  data: FundGroupTrialBalanceRow[]
  members: FundGroupLedgerMember[]
  finYear: FundLedgerFinYear | null
  memberFinYears: FundGroupMemberFinYear[]
  totals: (FundLedgerTotals & { eliminations?: number }) | null
  error?: string
}

export const fetchFundGroupTrialBalance = async (
  fundGroupId: string,
  params: { fyId?: string | null; asOf?: string | null; showAllocation?: boolean } = {},
  accessToken: string | null = null
): Promise<FundGroupTrialBalanceResponse> => {
  try {
    const query: Record<string, string> = {}
    if (params.fyId) query.fy_id = params.fyId
    if (params.asOf) query.as_of = params.asOf
    // the members' partner allocation lines of a year close: hidden by default
    if (params.showAllocation) query.show_allocation = 'true'
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.TRIAL_BALANCE, { id: fundGroupId }, query)
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.TRIAL_BALANCE.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch trial balance: ${response.status}`)
    const json = (await response.json()) as {
      data?: unknown[]
      members?: unknown[]
      fin_year?: unknown
      member_fin_years?: unknown[]
      totals?: unknown
    }
    return {
      data: validateFundGroupTrialBalanceRows(json.data ?? []),
      members: validateFundGroupLedgerMembers(json.members ?? []),
      finYear: (json.fin_year as FundLedgerFinYear) ?? null,
      memberFinYears: (json.member_fin_years as FundGroupMemberFinYear[]) ?? [],
      totals: (json.totals as FundLedgerTotals & { eliminations?: number }) ?? null
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching group trial balance:', error)
    return {
      data: [],
      members: [],
      finYear: null,
      memberFinYears: [],
      totals: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchFundGroupGlDetail = async (
  fundGroupId: string,
  glAccountId: string,
  params: { fyId?: string | null; asOf?: string | null; showAllocation?: boolean } = {},
  accessToken: string | null = null
): Promise<{ data: FundGroupGlDetailRow[]; opening?: number | null; openingFmt?: string | null; error?: string }> => {
  try {
    const query: Record<string, string> = { gl_account_id: glAccountId }
    if (params.fyId) query.fy_id = params.fyId
    if (params.asOf) query.as_of = params.asOf
    if (params.showAllocation) query.show_allocation = 'true'
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.GL_DETAIL, { id: fundGroupId }, query)
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.GL_DETAIL.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch GL detail: ${response.status}`)
    const json = (await response.json()) as { data?: unknown[]; opening?: number; opening_fmt?: string }
    return { data: validateFundGroupGlDetailRows(json.data ?? []), opening: json.opening ?? null, openingFmt: json.opening_fmt ?? null }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching group GL detail:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupStatementOptions = async (
  fundGroupId: string,
  accessToken: string | null = null
): Promise<FundStatementOptionsResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.STATEMENT_OPTIONS, { id: fundGroupId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.STATEMENT_OPTIONS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch statement options: ${response.status}`)
    const json = (await response.json()) as { statement_types?: unknown[]; lps?: unknown[] }
    return {
      statementTypes: validateFundStatementTypeOptions(json.statement_types ?? []),
      lps: validateFundStatementLpOptions(json.lps ?? [])
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching group statement options:', error)
    return { statementTypes: [], lps: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupStatement = async (
  fundGroupId: string,
  params: { code: string; fyId?: string | null; asOf?: string | null },
  accessToken: string | null = null
): Promise<FundStatementResponse> => {
  try {
    const query: Record<string, string> = { code: params.code }
    if (params.fyId) query.fy_id = params.fyId
    if (params.asOf) query.as_of = params.asOf
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.STATEMENT, { id: fundGroupId }, query)
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.STATEMENT.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch statement: ${response.status}`)
    const json = (await response.json()) as {
      data?: unknown[]
      fin_year?: unknown
      statement?: unknown
      layout?: unknown
      columns?: unknown[]
      totals?: Record<string, unknown>
      status_message?: string | null
    }
    return {
      data: validateFundStatementRows(json.data ?? []),
      finYear: (json.fin_year as FundLedgerFinYear) ?? null,
      statement: (json.statement as FundStatementMeta) ?? null,
      layout: (json.layout as FundStatementLayout) ?? null,
      columns: validateFundStatementColumns(json.columns ?? []),
      totals: json.totals ?? {},
      statusMessage: json.status_message ?? null
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching group statement:', error)
    return { data: [], finYear: null, statement: null, layout: null, columns: [], totals: {}, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupPartnersCapital = async (
  fundGroupId: string,
  params: { fyId?: string | null; asOf?: string | null } = {},
  accessToken: string | null = null
): Promise<FundPartnersCapitalResponse> => {
  try {
    const query: Record<string, string> = {}
    if (params.fyId) query.fy_id = params.fyId
    if (params.asOf) query.as_of = params.asOf
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.PARTNERS_CAPITAL, { id: fundGroupId }, query)
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.PARTNERS_CAPITAL.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch partners capital: ${response.status}`)
    const json = (await response.json()) as {
      data?: unknown[]
      fund_total?: unknown
      fin_year?: unknown
      allocation_note?: string | null
    }
    return {
      data: validateFundPartnersCapitalRows(json.data ?? []),
      fundTotal: (json.fund_total as FundPartnersCapitalTotal) ?? null,
      finYear: (json.fin_year as FundLedgerFinYear) ?? null,
      allocationNote: json.allocation_note ?? null
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching group partners capital:', error)
    return { data: [], fundTotal: null, finYear: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupLpCapitalAccount = async (
  fundGroupId: string,
  fundGroupCommitmentId: string,
  params: { fyId?: string | null; asOf?: string | null } = {},
  accessToken: string | null = null
): Promise<FundLpCapitalAccountResponse> => {
  try {
    const query: Record<string, string> = { fund_group_commitment_id: fundGroupCommitmentId }
    if (params.fyId) query.fy_id = params.fyId
    if (params.asOf) query.as_of = params.asOf
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.LP_CAPITAL_ACCOUNT, { id: fundGroupId }, query)
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.LP_CAPITAL_ACCOUNT.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch LP capital account: ${response.status}`)
    const json = (await response.json()) as {
      data?: unknown[]
      header?: unknown
      fin_year?: unknown
      columns?: unknown[]
      allocation_note?: string | null
      status_message?: string | null
    }
    return {
      data: validateFundStatementRows(json.data ?? []),
      header: (json.header as FundLpCapitalAccountHeader) ?? null,
      finYear: (json.fin_year as FundLedgerFinYear) ?? null,
      columns: validateFundStatementColumns(json.columns ?? []),
      allocationNote: json.allocation_note ?? null,
      statusMessage: json.status_message ?? null
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching group LP capital account:', error)
    return { data: [], header: null, finYear: null, columns: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** Whether the investor statements of a consolidated fund can be made for a period
 *  end (spec A3.14): reconciliation, what blocks, what only warns. */
export const fetchFundGroupStatementChecks = async (
  id: string,
  periodEnd: string,
  accessToken: string | null = null
): Promise<InvestorStatementChecksResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.STATEMENT_CHECKS, { id }, { period_end: periodEnd })
    const response = await fetch(url, { method: API_ROUTES.FUND_GROUPS.STATEMENT_CHECKS.method, headers })
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string }
      throw new Error(body.message || `Failed to fetch statement checks: ${response.status}`)
    }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateInvestorStatementChecks(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching statement checks:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * Investor statement settings of a consolidated fund (view__fund_group_statement_settings): the layout, the
 * partners' capital page, the entity
 * statements and the wording overrides, plus the layouts and
 * the default wording. The fields are written through InputManager's
 * auto-patch (API_ROUTES.FUND_GROUPS.STATEMENT_SETTINGS_PATCH), one at a time.
 */
export const fetchFundGroupStatementSettings = async (
  id: string,
  accessToken: string | null = null
): Promise<{ data: FundStatementSettings | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.STATEMENT_SETTINGS, { id }, {})
    const response = await fetch(url, { method: API_ROUTES.FUND_GROUPS.STATEMENT_SETTINGS.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = (await response.json().catch(() => ({}))) as { message?: string }
      throw new Error(body.message || `Failed to fetch statement settings: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null }
    return { data: validateFundStatementSettings(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching statement settings:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// ---------------------------------------------------------------------------
// Consolidated distributions (docs/WATERFALL_CARRY_DESIGN.md, phase 5)
// ---------------------------------------------------------------------------
// A consolidated fund's distribution is a header (fund_group_distributions)
// with one distributions row per legal entity; the LEAD row is the primary
// fund's. Every writer returns the LEAD's refreshed detail - the shape of
// GET /distributions/:id - so the standard distribution form re-hydrates
// from it; a server-side refusal surfaces as `error` with the RPC's own
// sentence (json.error || json.message), like the single-fund writers.

export interface FundGroupDistributionDetailResponse {
  data: DistributionDetail | null
  error?: string
}

const errorText = (error: unknown): string => (error instanceof Error ? error.message : 'An unexpected error occurred')

/** The consolidated distributions of a fund group (the Distributions tab). */
export const fetchFundGroupDistributions = async (
  fundGroupId: string,
  accessToken: string | null = null
): Promise<{ data: FundGroupDistributionRow[]; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.DISTRIBUTIONS, { id: fundGroupId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUND_GROUPS.DISTRIBUTIONS.method, headers: authHeaders(accessToken) })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch the consolidated distributions: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFundGroupDistributionRows(json.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching consolidated distributions:', error)
    return { data: [], error: errorText(error) }
  }
}

/**
 * The Booked realizations of every member fund a consolidated distribution
 * can name as its sources (each labelled with the fund that holds it).
 */
export const fetchFundGroupDistributionSourceOptions = async (
  fundGroupId: string,
  accessToken: string | null = null
): Promise<{ data: DistributionSourceOption[]; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.DISTRIBUTION_SOURCE_OPTIONS, { id: fundGroupId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_GROUPS.DISTRIBUTION_SOURCE_OPTIONS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch the realizations: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateDistributionSourceOptions(json.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching consolidated distribution source options:', error)
    return { data: [], error: errorText(error) }
  }
}

/**
 * Create (fund_group_id) or update (fund_group_distribution_id) a Draft
 * consolidated distribution: the lead's inputs - name, date, kind, gross,
 * the sources of any member fund, the SBA override reason. No overrides in
 * this phase: the server refuses them.
 */
export const saveFundGroupDistribution = async (
  payload: FundGroupDistributionSavePayload,
  accessToken: string | null = null
): Promise<FundGroupDistributionDetailResponse> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.DISTRIBUTION_SAVE), {
      method: API_ROUTES.FUND_GROUPS.DISTRIBUTION_SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; message?: string; error?: string }
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(json.error || json.message || `Failed to save the consolidated distribution: ${response.status}`)
    }
    if (!json.data) return { data: null }
    return { data: validateDistributionDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving consolidated distribution:', error)
    return { data: null, error: errorText(error) }
  }
}

type FundGroupDistributionActionRoute =
  | typeof API_ROUTES.FUND_GROUPS.DISTRIBUTION_COMPUTE
  | typeof API_ROUTES.FUND_GROUPS.DISTRIBUTION_ISSUE
  | typeof API_ROUTES.FUND_GROUPS.DISTRIBUTION_REVERT

/**
 * Lifecycle actions on the header (compute / issue / revert) return the
 * lead's refreshed detail on success; a refusal surfaces as `error` with its
 * sentence.
 */
const postFundGroupDistributionAction = async (
  route: FundGroupDistributionActionRoute,
  fundGroupDistributionId: string,
  accessToken: string | null
): Promise<FundGroupDistributionDetailResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, route, { id: fundGroupDistributionId }, {})
    const response = await fetch(url, { method: route.method, headers: authHeaders(accessToken) })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; message?: string; error?: string }
    if (!response.ok) {
      return { data: null, error: json.error || json.message || `Request failed: ${response.status}` }
    }
    if (!json.data) return { data: null }
    return { data: validateDistributionDetail(json.data) }
  } catch (error) {
    return { data: null, error: errorText(error) }
  }
}

export const computeFundGroupDistribution = (fundGroupDistributionId: string, accessToken: string | null = null) =>
  postFundGroupDistributionAction(API_ROUTES.FUND_GROUPS.DISTRIBUTION_COMPUTE, fundGroupDistributionId, accessToken)

export const issueFundGroupDistribution = (fundGroupDistributionId: string, accessToken: string | null = null) =>
  postFundGroupDistributionAction(API_ROUTES.FUND_GROUPS.DISTRIBUTION_ISSUE, fundGroupDistributionId, accessToken)

export const revertFundGroupDistribution = (fundGroupDistributionId: string, accessToken: string | null = null) =>
  postFundGroupDistributionAction(API_ROUTES.FUND_GROUPS.DISTRIBUTION_REVERT, fundGroupDistributionId, accessToken)

/** Soft-trash a Draft consolidated distribution and its entity rows. */
export const deleteFundGroupDistribution = async (
  fundGroupDistributionId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.DISTRIBUTION_DELETE, { id: fundGroupDistributionId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUND_GROUPS.DISTRIBUTION_DELETE.method, headers: authHeaders(accessToken) })
    if (!response.ok) {
      const json = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
      return { success: false, error: json.error || json.message || `Failed to delete the consolidated distribution: ${response.status}` }
    }
    return { success: true }
  } catch (error) {
    return { success: false, error: errorText(error) }
  }
}

/**
 * The Statements tab's Waterfall position panel of a consolidated fund: the
 * fund position model (wf__hypothetical_liquidation) run over the
 * consolidated fund, its id as fund_id. `data` is null without an open
 * waterfall; the engine's own sentence comes back as `error` where it cannot
 * run.
 */
export const fetchFundGroupWaterfallPosition = async (
  fundGroupId: string,
  asOf: string,
  accessToken: string | null = null
): Promise<{ data: FundWaterfallPosition | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_GROUPS.WATERFALL_POSITION, { id: fundGroupId }, { as_of: asOf })
    const response = await fetch(url, { method: API_ROUTES.FUND_GROUPS.WATERFALL_POSITION.method, headers: authHeaders(accessToken) })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
      throw new Error(body.error || body.message || `Failed to fetch the waterfall position: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null }
    return { data: validateFundWaterfallPosition(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching the consolidated waterfall position:', error)
    return { data: null, error: errorText(error) }
  }
}
